<template>
    <div class="application-show__wrapper">
        <div
            class="app__card_show application-show__item"
            :class="applicationTypeClasses.card"
            @click="toggleFullDisplayMode"
        >
            <div class="app__card-body application-show__content_short">
                <div class="application-show__icon" :class="applicationTypeClasses.icon">
                    <application-logo :version="currentVersion"></application-logo>
                </div>
                <div v-if="currentVersion" class="application-show__descr__wrapper">
                    <p class="application-show__paragraph application-show__title">
                        {{ application.name[currentLocale] }}
                    </p>
                    <p class="application-show__paragraph application-show__author">
                        <span v-if="application.author">{{
                            $t("applications.author", {
                                author: application.author,
                            })
                        }}</span>
                    </p>
                    <p class="application-show__paragraph application-show__paragraph-short">
                        {{ currentVersion.brief[currentLocale] }}
                    </p>
                </div>
            </div>
            <transition-expand>
                <div class="app__card-body application-show__content_full" v-if="isFullDisplayMode">
                    <p class="application-show__paragraph" v-if="applicationCategories.length">
                        {{ $t("applications.applicationCategories", {categories: applicationCategories}) }}
                    </p>

                    <p class="application-show__paragraph" v-if="currentVersion && currentVersion.description">
                        {{ currentVersion.description[currentLocale] }}
                    </p>
                    <hr class="application-show__separator" :class="applicationTypeClasses.separator" />
                    <div class="application-show__versions">
                        {{ $tc("words.versions") }}
                    </div>
                    <div class="application-show__version-list">
                        <dl>
                            <div v-for="version in application.versions" :key="version.id">
                                <dt>{{ version.versionNumber }} - {{ $t("versions.date", {date: version.date}) }}</dt>
                                <dd>
                                    <div class="application-show__paragraph" v-if="version.releaseNotes">
                                        {{ version.releaseNotes[currentLocale] }}
                                    </div>
                                    <a
                                        :href="version.url"
                                        target="_blank"
                                        class="application-show__link"
                                        :class="applicationTypeClasses.link"
                                    >
                                        {{ version.url }}
                                    </a>
                                </dd>
                            </div>
                        </dl>
                    </div>
                    <div class="application-show__footer">
                        <sah-button class="app__button_alt" @press="deleteApplication">{{
                            $tc("words.delete")
                        }}</sah-button>
                        <sah-button @press="$emit('editApplication', application)">{{ $tc("words.edit") }}</sah-button>
                    </div>
                </div>
            </transition-expand>
        </div>
    </div>
</template>

<script>
import {store, SahButton} from "atom";
import ApplicationLogo from "./ApplicationLogo";
import TransitionExpand from "./animated/TransitionExpand";
const {createNamespacedHelpers} = store;
const {mapActions, mapGetters, mapState} = createNamespacedHelpers("arcman");
export default {
    name: "application-show",
    props: {
        application: {type: Object, required: true},
    },
    components: {
        ApplicationLogo,
        TransitionExpand,
        SahButton,
    },
    computed: {
        ...mapGetters(["currentLocale"]),
        ...mapState(["arcologyConstants"]),
        hasVersions() {
            return this.application.versions?.length;
        },
        hasMultipleVersions() {
            return this.application.versions?.length > 1;
        },
        currentVersion() {
            return this.application.versions[this.currentVersionIndex];
        },
        applicationFields() {
            return this.arcologyConstants.APPLICATION_FIELDS.filter(field => {
                return field.type !== "custom";
            });
        },
        applicationCategories() {
            return this.application.categories
                .filter(element => element?.[this.currentLocale]?.trim().length)
                .map(element => element[this.currentLocale])
                .join(", ");
        },
        applicationContexts() {
            return this.application.executionContext.join(" | ");
        },
        applicationTypeSuffix() {
            return this.application.type.split(" ").join("-");
        },
        applicationTypeClasses() {
            return {
                card: `application-show_${this.applicationTypeSuffix}`,
                header: `application-show__card-header_${this.applicationTypeSuffix}`,
                icon: `application-show__icon_${this.applicationTypeSuffix}`,
                separator: `application-show__separator_${this.applicationTypeSuffix}`,
                versionBadge: `application-show__version-badge_${this.applicationTypeSuffix}`,
            };
        },
        versionsTabs() {
            return this.application.versions.map((element, index) => ({
                id: `${element.id}${index}`,
                displayName:
                    element.stage && element.stage !== "production"
                        ? `${element.versionNumber} - ${element.stage}`
                        : element.versionNumber,
            }));
        },
    },
    data: () => {
        return {
            currentVersionIndex: 0,
            isFullDisplayMode: false,
        };
    },
    methods: {
        ...mapActions({
            destroyApplication: "DELETE_APPLICATION",
        }),
        deleteApplication() {
            this.$modal
                .dialog(this.$t("applications.confirmDelete", {name: this.application.name[this.currentLocale]}))
                .then(confirm => {
                    if (confirm) {
                        this.destroyApplication(this.application);
                    }
                    this.$modal.hide();
                });
        },
        toggleFullDisplayMode() {
            this.isFullDisplayMode = !this.isFullDisplayMode;
        },
    },
};
</script>

<style lang="scss" scoped>
.application-show__wrapper::v-deep {
    @apply overflow-hidden p-2;
    .application-show__item:hover {
        @apply cursor-pointer;
        box-shadow: $application-show-card-shadow;
    }

    .application-show__content_full {
        @apply py-0;
    }
    .application-show__descr__wrapper {
        @apply relative;
    }
    .application-show__paragraph {
        @apply m-0 px-0 pt-0 pb-2 overflow-hidden whitespace-pre-line;

        &.application-show__paragraph-short {
            @apply overflow-hidden;
            max-height: $application-show-brief-max-height;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
        }

        &.application-show__paragraph-tech {
            @apply italic;
            color: $text-author-color;
            font-size: $font-small;
        }
    }

    .application-show_binary {
        border-color: $border-application-binary-card-color;
    }

    .application-show_webapp {
        border-color: $border-application-webapp-card-color;
    }

    .application-show_remote-html {
        border-color: $border-card-color;
    }

    .application-show__display-mode-toggle {
        &:hover {
            @apply cursor-pointer;
        }
    }

    .application-show__title {
        @apply font-bold;
        font-size: $font-medium;
    }

    .application-show__author {
        @apply italic;
        color: $text-author-color;
    }

    .application-show__icon_space_after {
        @apply mr-4;
    }

    .application-show__icon_space_around {
        @apply mx-4;
    }

    .application-show__content_short {
        @apply flex;
        .application-show__icon {
            @apply flex items-center justify-center rounded-lg mr-4;
            border: 1px solid $border-card-icon-color;
            min-width: $application-show-icon-min-width;
            max-width: $application-show-icon-max-width;
            height: $application-show-icon-height;
        }

        .application-show__icon_binary {
            background-color: $background-application-binary-card-header-color;
        }

        .application-show__icon_webapp {
            background-color: $background-application-webapp-card-header-color;
        }

        .application-show__icon_remote-html {
            background-color: inherit;
        }
    }

    .application-show__content_full {
        @apply h-auto cursor-default;

        .application-show__icon {
            @apply float-none inline-block whitespace-no-wrap w-full align-middle;

            & > img {
                @apply align-middle;
            }

            .application-show__icon-size {
                width: 25%;
            }
            .application-show__icon-url {
                width: 75%;
            }
        }
    }

    .application-display-field {
        @apply flex w-full mb-1;
    }

    .application-display-field__label {
        @apply font-bold mr-2;
    }

    .application-display-field__value {
        @apply break-all;
    }

    .application-show__separator {
        @apply -mx-3 block border-0;
        height: $separator-height;
    }

    .application-show__separator_binary {
        background-color: $background-application-binary-card-header-color;
    }

    .application-show__separator_webapp {
        background-color: $background-application-webapp-card-header-color;
    }

    .application-show__separator_remote-html {
        background-color: $background-application-remote-html-card-header-color;
    }

    .application-show__versions {
        @apply font-bold;
    }
    .application-show__versions-badges {
        @apply my-2;
    }

    .application-show__version-badge {
        @apply p-2 mr-2 mb-2 rounded-lg text-white opacity-75;
        &:hover {
            @apply cursor-pointer;
        }
    }

    .application-show__version-badge_new_version {
        @extend .application-show__version-badge;
        @apply inline-block my-2 opacity-100;
    }

    .application-show__version-badge_binary {
        background-color: $background-application-binary-card-header-color;
    }

    .application-show__version-badge_webapp {
        background-color: $background-application-webapp-card-header-color;
    }

    .application-show__version-badge_remote-html {
        background-color: $background-application-remote-html-card-header-color;
    }

    .slide-group__content {
        @apply flex flex-row flex-wrap;
    }

    .tab-item_active {
        @apply opacity-100;
    }

    .application-show__footer {
        @apply block text-right my-2;
    }

    .application-show__version-actions {
        @apply float-right cursor-pointer;
    }

    .application-show__link {
        @apply break-words;
    }

    .application-show__version-list dt {
        @apply font-bold;
    }
}
</style>
